import { type FC } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { type AccountOpeningTypeSelector, type AccountType } from '../../../models/AccountOpening'
import { axisOptions, defaultNameSource, holderSlots, resolveTypeId } from '../domain/accountTypeCatalog'
import { type ContactOption, type WizardAccount, type WizardHolder } from '../domain/types'

interface AccountTypeFieldsProps {
  accountTypes: AccountType[]
  selectors: AccountOpeningTypeSelector[]
  account: WizardAccount
  onChange: (account: WizardAccount) => void
  contactOptions: ContactOption[]
  defaultHolderName?: string | null
  defaultHolderContactId?: string | null
}

export const AccountTypeFields: FC<AccountTypeFieldsProps> = ({
  accountTypes,
  selectors,
  account,
  onChange,
  contactOptions,
  defaultHolderName,
  defaultHolderContactId,
}) => {
  const axisWidth = selectors.length > 2 ? 4 : 6

  const axisEnabled = (axisIndex: number) => selectors.slice(0, axisIndex).every((selector) => !!account.selection[selector.field])

  const handleAxisChange = (axisIndex: number, value: string) => {
    const selection = { ...account.selection, [selectors[axisIndex].field]: value }
    selectors.slice(axisIndex + 1).forEach((selector) => delete selection[selector.field])

    const typeId = resolveTypeId(accountTypes, selectors, selection)
    const slots = holderSlots(accountTypes, typeId)
    const launchSlot = slots.findIndex((slot) => slot.kind === 'individual')
    const holders: WizardHolder[] = slots.map((slot, index) => {
      if (index === launchSlot && defaultHolderContactId) {
        return { roleCode: slot.roleCode, nameSource: 'contact', name: defaultHolderName ?? '', contactId: defaultHolderContactId }
      }
      return { roleCode: slot.roleCode, nameSource: slot.kind === 'organization' ? 'manual' : defaultNameSource(contactOptions), name: '', contactId: null }
    })
    onChange({ ...account, selection, typeId, holders, schemaSnapshot: {}, schemaFetchedAt: undefined, values: {} })
  }

  return (
    <Row className='g-3'>
      {selectors.map((selector, axisIndex) => {
        const options = axisOptions(accountTypes, selectors, account.selection, axisIndex)
        const chosen = account.selection[selector.field] ?? ''
        const enabled = axisEnabled(axisIndex)
        return (
          <Col key={selector.field} md={axisWidth} className='d-flex flex-column zocks-gap-0.5'>
            <Form.Label className='paragraph-small mb-0'>{selector.label}</Form.Label>
            <Form.Select value={chosen} disabled={!enabled || options.length === 0} onChange={(event) => handleAxisChange(axisIndex, event.target.value)}>
              <option value=''>{selector.placeholder ?? `Select a ${selector.label.toLowerCase()}…`}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Col>
        )
      })}
    </Row>
  )
}
